import { useQuery } from '@tanstack/react-query';

import { agentExecutionsService } from '../services/agentExecutions';

export const useAgentExecutions = ({
  instanceId,
  agentId,
  page = 1,
  pageSize = 100,
}: {
  instanceId: string;
  agentId?: string;
  page?: number;
  pageSize?: number;
}) => {
  return useQuery({
    queryKey: ['agentExecutions', instanceId, agentId, page, pageSize],
    queryFn: () => {
      return agentExecutionsService.getExecutions({ instanceId, agentId, page, pageSize });
    },
    enabled: !!instanceId,
    staleTime: 1000 * 60, // Cache for 1 minute
    refetchInterval: 5000, // Refetch every 5 seconds
    retry: 1,
  });
};

export const useExecutionDetails = (executionUrl?: string, shouldPoll = false) => {
  return useQuery({
    queryKey: ['executionDetails', executionUrl],
    queryFn: () => {
      if (!executionUrl) throw new Error('executionUrl is required');
      return agentExecutionsService.getExecutionDetails(executionUrl);
    },
    enabled: !!executionUrl,
    staleTime: shouldPoll ? 0 : 1000 * 60, // No caching when polling, otherwise cache for 1 minute
    refetchInterval: shouldPoll ? 5000 : false, // Poll every 5 seconds when shouldPoll is true
    retry: 1,
  });
};

export const useMemoryDetails = (memoryUrl?: string, shouldPoll = false) => {
  return useQuery({
    queryKey: ['memoryDetails', memoryUrl],
    queryFn: () => {
      if (!memoryUrl) throw new Error('memoryUrl is required');
      return agentExecutionsService.getMemoryDetails(memoryUrl);
    },
    enabled: !!memoryUrl,
    staleTime: shouldPoll ? 0 : 1000 * 60, // No caching when polling, otherwise cache for 1 minute
    refetchInterval: shouldPoll ? 5000 : false, // Poll every 5 seconds when shouldPoll is true
    retry: 1,
  });
};

export const useMemoryChunks = (chunksUrl?: string, shouldPoll = false) => {
  return useQuery({
    queryKey: ['memoryChunks', chunksUrl],
    queryFn: () => {
      if (!chunksUrl) throw new Error('chunksUrl is required');
      return agentExecutionsService.getMemoryChunks(chunksUrl);
    },
    enabled: !!chunksUrl,
    staleTime: shouldPoll ? 0 : 1000 * 60, // No caching when polling, otherwise cache for 1 minute
    refetchInterval: shouldPoll ? 5000 : false, // Poll every 5 seconds when shouldPoll is true
    retry: 1,
  });
};

// Hook specifically for specialized agent executions
export const useSpecializedAgentExecutions = (
  instanceId: string,
  agentId: string,
  page = 1,
  pageSize = 20
) => {
  return useQuery({
    queryKey: ['specializedAgentExecutions', instanceId, agentId, page, pageSize],
    queryFn: () => agentExecutionsService.getAgentExecutions(instanceId, agentId, page, pageSize),
    enabled: !!instanceId && !!agentId,
    staleTime: 1000 * 60, // Cache for 1 minute
    refetchInterval: 5000, // Refetch every 5 seconds for live updates
    retry: 1,
  });
};
